<script lang="ts">
	import { TalksRoutes } from '#lib/websites/talks/constants/routes.js';
	import { ApplePodcastsIcon } from '$global/assets/icons/ApplePodcastsIcon';
	import { SpotifyIcon } from '$global/assets/icons/SpotifyIcon';
	import YouTubeIcon from '$global/assets/icons/YouTubeIcon/YouTubeIcon.svelte';
	import { Txt } from '$global/components/Txt';
	import { VideoCard } from '$global/components/VideoCard';
	import { getImageUrl } from '$global/utils/getImageUrl';
	import type { PodcastPageData } from '$routes/talks/podcasts/[slug]/types';

	interface Props {
		// Without the tokens: this card shows the description as plain text, and
		// the episode page's related episodes carry none.
		podcastEpisode: Omit<
			NonNullable<PodcastPageData['podcast']['episodes']>[number],
			'shortDescriptionTokens'
		>;
		seriesAndDate?: string;
		class?: string;
	}

	let { podcastEpisode, seriesAndDate, ...rest }: Props = $props();

	// The whole card is a link, and a link inside a link is invalid HTML: the
	// browser ends the card's link at the inner one, the page no longer matches
	// what Svelte hydrates and the error page replaces it. The platform links
	// are buttons instead, like the desktop card's.
	const openPlatform = (event: MouseEvent, url: string | null | undefined) => {
		event.preventDefault();

		if (url) {
			window.open(url, '_blank', 'noopener noreferrer');
		}
	};
</script>

<VideoCard
	variant="light"
	class={rest.class}
	description={`${podcastEpisode?.short_description}`}
	href={`${TalksRoutes.Podcasts}/${podcastEpisode?.podcast?.slug}/episodes/${podcastEpisode?.slug}`}
	title={`${podcastEpisode?.title}`}
	src={getImageUrl(podcastEpisode?.image?.filename_disk)}
	dataCy="mobile-podcast-episode-card"
	duration={`${podcastEpisode?.duration}`}
	classes={{ textContainerClass: 'bg-white' }}
>
	{#if seriesAndDate}
		<Txt
			variant="label4"
			class="mt-2 text-gray-500"
			dataCy="mobile-podcast-episode-card-series-and-date"
		>
			{seriesAndDate}
		</Txt>
	{/if}
	<Txt class="mt-4 underline" variant="label3">View episode</Txt>
	<div class="mt-6 flex gap-3">
		{#if podcastEpisode?.youtube_link}
			<button
				type="button"
				aria-label="Watch on YouTube"
				onclick={(event) => openPlatform(event, podcastEpisode?.youtube_link)}
			>
				<YouTubeIcon class="scale-[1.3]" hexCode="#FF0000" />
			</button>
		{/if}
		{#if podcastEpisode?.spotify_link}
			<button
				type="button"
				aria-label="Listen on Spotify"
				onclick={(event) => openPlatform(event, podcastEpisode?.spotify_link)}
			>
				<SpotifyIcon />
			</button>
		{/if}
		{#if podcastEpisode?.apple_podcasts_link}
			<button
				type="button"
				aria-label="Listen on Apple Podcasts"
				onclick={(event) =>
					openPlatform(event, podcastEpisode?.apple_podcasts_link)}
			>
				<ApplePodcastsIcon />
			</button>
		{/if}
	</div>
</VideoCard>
